/**
 * CalendarModal — full-screen calendar modal opened from HomeHero date click.
 *
 * Dual-calendar (Gregorian + Hijri) month grid with events overlay.
 * Events come from EventsService = Nager.Date holidays + IslamicCalendar
 * offline + personal localStorage.
 *
 * Customization (gear icon top-right):
 *   - Display mode: Gregorian / Both / Hijri
 *   - Week starts on: Mon / Sat / Sun
 *   - Toggle: holidays / Islamic / personal
 *   - Country code (auto-detected from LocationService, user-overridable)
 *
 * No routing — modal overlay (LocationPrimingCard pattern). Closed via
 * close button, escape key, or backdrop click.
 */

import type { AweniContext } from '../../types';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
import { hijriDateRange, formatHijriRange } from '../../services/HijriService';
import { EventsService, getEventsService } from '../../services/EventsService';
import type { CalendarEvent } from './types';

/** Country-code → display name + "Hijri authority" subtitle. The
 *  subtitle is the body that publishes the official Hijri date for that
 *  country (matches what AWENI's HijriController proxies via Aladhan). */
const HIJRI_AUTHORITY: Record<string, { country: string; authority: string; flag: string }> = {
  MA: { country: 'Morocco', authority: 'HCMM moonsighting', flag: '🇲🇦' },
  DZ: { country: 'Algeria', authority: 'local moonsighting', flag: '🇩🇿' },
  TN: { country: 'Tunisia', authority: 'Mufti of the Republic', flag: '🇹🇳' },
  EG: { country: 'Egypt', authority: 'Dar al-Iftāʾ', flag: '🇪🇬' },
  SA: { country: 'Saudi Arabia', authority: 'Umm al-Qura', flag: '🇸🇦' },
  AE: { country: 'UAE', authority: 'Umm al-Qura', flag: '🇦🇪' },
  KW: { country: 'Kuwait', authority: 'Umm al-Qura', flag: '🇰🇼' },
  QA: { country: 'Qatar', authority: 'Umm al-Qura', flag: '🇶🇦' },
  BH: { country: 'Bahrain', authority: 'Umm al-Qura', flag: '🇧🇭' },
  OM: { country: 'Oman', authority: 'local moonsighting', flag: '🇴🇲' },
  JO: { country: 'Jordan', authority: 'Iftāʾ', flag: '🇯🇴' },
  TR: { country: 'Türkiye', authority: 'Diyanet (calculated)', flag: '🇹🇷' },
  PK: { country: 'Pakistan', authority: 'Ruet-e-Hilal', flag: '🇵🇰' },
  IN: { country: 'India', authority: 'local moonsighting', flag: '🇮🇳' },
  BD: { country: 'Bangladesh', authority: 'local moonsighting', flag: '🇧🇩' },
  ID: { country: 'Indonesia', authority: 'Kemenag', flag: '🇮🇩' },
  MY: { country: 'Malaysia', authority: 'JAKIM', flag: '🇲🇾' },
  US: { country: 'United States', authority: 'ISNA Fiqh Council', flag: '🇺🇸' },
  CA: { country: 'Canada', authority: 'ISNA Fiqh Council', flag: '🇨🇦' },
  GB: { country: 'United Kingdom', authority: 'Moonsighting UK', flag: '🇬🇧' },
  FR: { country: 'France', authority: 'CFCM / regional', flag: '🇫🇷' },
  DE: { country: 'Germany', authority: 'regional', flag: '🇩🇪' },
};

/** Read country code + city from cached LocationService state (sync).
 *  Returns `needsLocation:true` when no country is set — the calendar
 *  surfaces a "set your city" prompt because Hijri dates differ between
 *  authorities (Saudi UAQ vs Morocco HCMM can differ by 1–2 days). */
function getLocationLabel(): { city: string | null; cc: string | null; auth: string | null; flag: string | null; needsLocation: boolean } {
  try {
    const raw = localStorage.getItem('aweni_location');
    if (!raw) return { city: null, cc: null, auth: null, flag: null, needsLocation: true };
    const s = JSON.parse(raw) as { city?: string | null; countryCode?: string | null };
    const cc = (s.countryCode || '').toUpperCase() || null;
    if (!cc) return { city: s.city || null, cc: null, auth: null, flag: null, needsLocation: true };
    const ha = HIJRI_AUTHORITY[cc] || null;
    return {
      city: s.city || null,
      cc,
      auth: ha ? `${ha.country} · ${ha.authority}` : `${cc} · regional`,
      flag: ha ? ha.flag : null,
      needsLocation: false,
    };
  } catch { return { city: null, cc: null, auth: null, flag: null, needsLocation: true }; }
}

interface CalendarPrefs {
  mode: 'gregorian' | 'both' | 'hijri';
  weekStart: 0 | 1 | 6; // 0=Sun, 1=Mon, 6=Sat
  showHolidays: boolean;
  showIslamic: boolean;
  showPersonal: boolean;
  /** ISO 3166-1 alpha-2 — null = no holiday filtering. */
  countryCode: string | null;
}

const PREFS_LS_KEY = 'aweni_calendar_prefs';

const DEFAULT_PREFS: CalendarPrefs = {
  mode: 'both',
  weekStart: 1, // Mon by default; Saudi/Gulf/Islamic users can switch to Sat.
  showHolidays: true,
  showIslamic: true,
  showPersonal: true,
  countryCode: null,
};

export interface CalendarModalDeps {
  ctx: AweniContext;
  /** Optional starting date (defaults to today). */
  initialDate?: Date;
  /** ISO 3166-1 alpha-2 to use as default country (from LocationService). */
  defaultCountryCode?: string | null;
}

export class CalendarModal {
  private overlay: HTMLElement | null = null;
  private deps: CalendarModalDeps;
  private events: EventsService;
  private prefs: CalendarPrefs;
  /** First-of-month for the currently displayed month. */
  private cursor: Date;
  private selectedISO: string;
  private monthEvents: CalendarEvent[] = [];
  private settingsOpen = false;
  private addOpen = false;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(deps: CalendarModalDeps) {
    this.deps = deps;
    this.events = getEventsService();
    this.prefs = this.loadPrefs(deps.defaultCountryCode || null);
    const start = deps.initialDate ?? new Date();
    this.selectedISO = toISO(start);
    this.cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  }

  show(): void {
    if (this.overlay) return;
    this.injectStyles();

    const isRtl = this.deps.ctx.i18n.dir === 'rtl';
    const overlay = document.createElement('div');
    overlay.className = 'aweni-cal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'aweni-cal-title');
    overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });

    document.body.appendChild(overlay);
    this.overlay = overlay;

    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.addOpen) { this.addOpen = false; this.render(); return; }
        if (this.settingsOpen) { this.settingsOpen = false; this.render(); return; }
        this.close();
      }
    };
    document.addEventListener('keydown', this.escHandler);

    this.render();
    // Lazy: load events for the visible month from cache, then refresh in bg.
    this.refreshMonthEvents();
  }

  close(): void {
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.overlay?.remove();
    this.overlay = null;
  }

  // -------- Render --------------------------------------------------------

  private render(): void {
    if (!this.overlay) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';

    if (this.settingsOpen) {
      this.overlay.innerHTML = this.renderSettings(t, isRtl);
      this.bindSettings();
      return;
    }
    if (this.addOpen) {
      this.overlay.innerHTML = this.renderAddEvent(t, isRtl);
      this.bindAddEvent();
      return;
    }

    this.overlay.innerHTML = this.renderMonth(t, isRtl);
    this.bindMonth();
  }

  // -------- Month view ---------------------------------------------------

  private renderMonth(t: (k: string, p?: Record<string, string | number>) => string, _isRtl: boolean): string {
    const locale = this.deps.ctx.i18n.locale || 'en';
    const monthName = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(this.cursor);
    const hijriCursor = gregorianToHijri(this.cursor);
    const hijriCursorLabel = formatHijri(hijriCursor, locale);

    const grid = this.buildMonthGrid();
    const dayHeaders = this.dayHeaderLabels(locale);

    const cells = grid.map((cell) => this.renderCell(cell, locale)).join('');
    const dayLabels = dayHeaders.map((d) => `<div class="aweni-cal-dayheader">${d}</div>`).join('');

    const selectedDay = this.findCell(this.selectedISO);
    const detailsHtml = selectedDay
      ? this.renderDetails(selectedDay.date, locale, t)
      : '';

    // Hijri country/authority reference — surfaces *where* the displayed
    // Hijri dates are calibrated for. Critical for trust (different
    // authorities can disagree by ±1 day on Eid / Ramadan boundaries).
    // When no country is set, show a tappable prompt so the user knows
    // (a) the displayed date is the offline fallback, and (b) how to fix.
    const locLabel = getLocationLabel();
    const refLine = locLabel.needsLocation
      ? `<button type="button" class="aweni-cal-ref aweni-cal-ref-prompt" data-action="set-location"
           title="${escapeHtml(t('calendar_hijri_ref_prompt_tooltip') || 'Hijri dates differ between countries (Saudi UAQ vs Morocco HCMM can differ by 1–2 days). Set your city to use the local authority.')}">
           <span class="aweni-cal-ref-flag" aria-hidden="true">📍</span>
           <span class="aweni-cal-ref-auth">${escapeHtml(t('calendar_hijri_ref_prompt') || 'Set your city for accurate Hijri date')}</span>
           <span class="aweni-cal-ref-cta">→</span>
         </button>`
      : `<div class="aweni-cal-ref" title="${escapeHtml(t('calendar_hijri_ref_tooltip') || 'Hijri reference: country authority used for moonsighting / calculation')}">
           ${locLabel.flag ? `<span class="aweni-cal-ref-flag">${locLabel.flag}</span>` : ''}
           <span class="aweni-cal-ref-city">${escapeHtml(locLabel.city || '')}</span>
           ${locLabel.city ? '<span class="aweni-cal-ref-sep">·</span>' : ''}
           <span class="aweni-cal-ref-auth">${escapeHtml(locLabel.auth || '')}</span>
         </div>`;

    return `
      <div class="aweni-cal-sheet" role="document">
        <header class="aweni-cal-header">
          <button type="button" class="aweni-cal-iconbtn" data-action="prev" aria-label="${t('calendar_prev_month')}">
            ${this.iconChevron('left')}
          </button>
          <div class="aweni-cal-titlewrap">
            <h2 id="aweni-cal-title" class="aweni-cal-title">${monthName}</h2>
            ${this.prefs.mode !== 'gregorian' ? `<div class="aweni-cal-subtitle">${hijriCursorLabel}</div>` : ''}
            ${refLine}
          </div>
          <button type="button" class="aweni-cal-iconbtn" data-action="next" aria-label="${t('calendar_next_month')}">
            ${this.iconChevron('right')}
          </button>
          <button type="button" class="aweni-cal-iconbtn" data-action="today" aria-label="${t('calendar_today')}" title="${t('calendar_today')}">
            ${this.iconCircle()}
          </button>
          <button type="button" class="aweni-cal-iconbtn" data-action="settings" aria-label="${t('calendar_settings')}">
            ${this.iconGear()}
          </button>
          <button type="button" class="aweni-cal-iconbtn aweni-cal-close" data-action="close" aria-label="${t('common_close')}">
            ${this.iconX()}
          </button>
        </header>

        <div class="aweni-cal-grid-wrap">
          <div class="aweni-cal-dayheaders">${dayLabels}</div>
          <div class="aweni-cal-grid">${cells}</div>
        </div>

        <section class="aweni-cal-details" aria-live="polite">
          ${detailsHtml}
        </section>
      </div>
    `;
  }

  private renderCell(cell: GridCell, locale: string): string {
    const dayEvents = this.monthEvents.filter((e) => e.date === cell.iso);
    const dotColors = unique(dayEvents.map((e) => categoryColor(e.category))).slice(0, 3);
    const dotsHtml = dotColors.map((c) => `<span class="aweni-cal-dot" style="background:${c}"></span>`).join('');
    const isSelected = cell.iso === this.selectedISO;
    const isToday = cell.iso === toISO(new Date());
    const isOther = !cell.inMonth;
    const classes = [
      'aweni-cal-cell',
      isSelected ? 'is-selected' : '',
      isToday ? 'is-today' : '',
      isOther ? 'is-other' : '',
    ].filter(Boolean).join(' ');

    const showG = this.prefs.mode !== 'hijri';
    const showH = this.prefs.mode !== 'gregorian';

    const gregNum = cell.date.getDate();
    const hijri = gregorianToHijri(cell.date);

    // Localized digits for Hijri annotation per locale (Arabic uses Western digits in our app)
    void locale;

    return `
      <button type="button" class="${classes}" data-iso="${cell.iso}" aria-pressed="${isSelected}">
        ${showG ? `<span class="aweni-cal-cell-greg">${gregNum}</span>` : ''}
        ${showH ? `<span class="aweni-cal-cell-hijri">${hijri.day}</span>` : ''}
        ${dotsHtml ? `<span class="aweni-cal-dots">${dotsHtml}</span>` : ''}
      </button>
    `;
  }

  private renderDetails(date: Date, locale: string, t: (k: string, p?: Record<string, string | number>) => string): string {
    const iso = toISO(date);
    const events = this.monthEvents.filter((e) => e.date === iso);
    const dayLabel = new Intl.DateTimeFormat(locale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(date);
    const hijri = formatHijri(gregorianToHijri(date), locale);

    // Future-event uncertainty hint — for any Hijri date that falls in a
    // FUTURE Hijri month (≥1 month away from today), show that the actual
    // Gregorian landing day can vary by ±1 to ±2 because each intermediate
    // Hijri month can be 29 OR 30 days. Algorithm + alternation heuristic
    // live in HijriService.hijriDateRange. We only show the hint for
    // SAME-DAY entries (i.e., when the user taps a future Hijri-only event)
    // — the day-cell tap already shows a Gregorian date, so we add the
    // "or YYYY-MM-DD" annotation when applicable.
    const hijriObj = gregorianToHijri(date);
    const range = hijriDateRange(hijriObj.day, hijriObj.month, hijriObj.year);
    const uncertaintyHint = range && range.alt && range.uncertainty !== '±0'
      ? `<div class="aweni-cal-details-uncertain" title="${escapeHtml(t('calendar_hijri_uncertain_tooltip') || 'Hijri months can be 29 or 30 days. The actual Gregorian date may shift by ±1 to ±2 days as the local moonsighting confirms each month.')}">
          ⚠ ${escapeHtml(t('calendar_hijri_uncertain') || 'Possible alternate date:')} ${escapeHtml(formatHijriRange(range, locale))}
         </div>`
      : '';

    const items = events.length
      ? events.map((e) => this.renderEventItem(e, t)).join('')
      : `<li class="aweni-cal-event aweni-cal-event-empty">${t('calendar_no_events')}</li>`;

    return `
      <div class="aweni-cal-details-head">
        <div class="aweni-cal-details-title">${dayLabel}</div>
        <div class="aweni-cal-details-sub">${hijri}</div>
        ${uncertaintyHint}
      </div>
      <ul class="aweni-cal-events" role="list">${items}</ul>
      <button type="button" class="aweni-cal-add" data-action="add" aria-label="${t('calendar_add_event')}">
        ${this.iconPlus()} <span>${t('calendar_add_event')}</span>
      </button>
    `;
  }

  private renderEventItem(e: CalendarEvent, t: (k: string) => string): string {
    const color = categoryColor(e.category);
    const tag = t(`calendar_cat_${e.category}`);
    const removeBtn = e.source === 'personal'
      ? `<button type="button" class="aweni-cal-event-del" data-event-del="${e.id}" aria-label="${t('common_delete')}">${this.iconTrash()}</button>`
      : '';
    return `
      <li class="aweni-cal-event">
        <span class="aweni-cal-event-bullet" style="background:${color}"></span>
        <div class="aweni-cal-event-body">
          <div class="aweni-cal-event-title">${escapeHtml(e.title)}</div>
          <div class="aweni-cal-event-meta">${tag}${e.description ? ` · ${escapeHtml(e.description)}` : ''}</div>
        </div>
        ${removeBtn}
      </li>
    `;
  }

  private bindMonth(): void {
    if (!this.overlay) return;
    this.overlay.querySelector('[data-action="prev"]')?.addEventListener('click', () => this.shiftMonth(-1));
    this.overlay.querySelector('[data-action="next"]')?.addEventListener('click', () => this.shiftMonth(1));
    this.overlay.querySelector('[data-action="today"]')?.addEventListener('click', () => this.goToday());
    this.overlay.querySelector('[data-action="settings"]')?.addEventListener('click', () => {
      this.settingsOpen = true; this.render();
    });
    this.overlay.querySelector('[data-action="close"]')?.addEventListener('click', () => this.close());
    this.overlay.querySelector('[data-action="add"]')?.addEventListener('click', () => {
      this.addOpen = true; this.render();
    });
    // "Set your city" prompt — close the modal and emit a doc-level event
    // that HomeHero listens for to open the LocationPrimingCard. Decoupled
    // pattern keeps CalendarModal independent of the priming flow.
    this.overlay.querySelector('[data-action="set-location"]')?.addEventListener('click', () => {
      this.close();
      try {
        document.dispatchEvent(new CustomEvent('aweni:request-location-priming', { detail: { source: 'calendar' } }));
      } catch { /* */ }
    });

    // Day cells
    this.overlay.querySelectorAll('.aweni-cal-cell').forEach((el) => {
      el.addEventListener('click', () => {
        const iso = (el as HTMLElement).getAttribute('data-iso');
        if (!iso) return;
        this.selectedISO = iso;
        // If day is in another month, also shift the cursor
        const d = parseISO(iso);
        if (d && (d.getMonth() !== this.cursor.getMonth() || d.getFullYear() !== this.cursor.getFullYear())) {
          this.cursor = new Date(d.getFullYear(), d.getMonth(), 1);
          this.refreshMonthEvents();
        }
        this.render();
      });
    });

    // Personal event deletion
    this.overlay.querySelectorAll('[data-event-del]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (el as HTMLElement).getAttribute('data-event-del');
        if (!id) return;
        this.events.removePersonalEvent(id);
        this.refreshMonthEvents();
      });
    });
  }

  // -------- Settings -----------------------------------------------------

  private renderSettings(t: (k: string, p?: Record<string, string | number>) => string, _isRtl: boolean): string {
    const p = this.prefs;
    return `
      <div class="aweni-cal-sheet" role="document">
        <header class="aweni-cal-header">
          <button type="button" class="aweni-cal-iconbtn" data-action="back-from-settings" aria-label="${t('common_back')}">
            ${this.iconChevron('left')}
          </button>
          <div class="aweni-cal-titlewrap">
            <h2 class="aweni-cal-title">${t('calendar_settings_title')}</h2>
          </div>
          <button type="button" class="aweni-cal-iconbtn aweni-cal-close" data-action="close" aria-label="${t('common_close')}">
            ${this.iconX()}
          </button>
        </header>

        <div class="aweni-cal-settings">
          <section class="aweni-cal-setrow">
            <label class="aweni-cal-label">${t('calendar_setting_mode')}</label>
            <div class="aweni-cal-segmented" role="radiogroup">
              ${this.segBtn('mode', 'gregorian', t('calendar_mode_gregorian'), p.mode === 'gregorian')}
              ${this.segBtn('mode', 'both', t('calendar_mode_both'), p.mode === 'both')}
              ${this.segBtn('mode', 'hijri', t('calendar_mode_hijri'), p.mode === 'hijri')}
            </div>
          </section>

          <section class="aweni-cal-setrow">
            <label class="aweni-cal-label">${t('calendar_setting_week_start')}</label>
            <div class="aweni-cal-segmented" role="radiogroup">
              ${this.segBtn('weekStart', '1', t('calendar_day_mon'), p.weekStart === 1)}
              ${this.segBtn('weekStart', '6', t('calendar_day_sat'), p.weekStart === 6)}
              ${this.segBtn('weekStart', '0', t('calendar_day_sun'), p.weekStart === 0)}
            </div>
          </section>

          <section class="aweni-cal-setrow">
            <label class="aweni-cal-label">${t('calendar_setting_layers')}</label>
            <label class="aweni-cal-toggle">
              <input type="checkbox" data-toggle="showHolidays" ${p.showHolidays ? 'checked' : ''}>
              <span>${t('calendar_layer_holidays')}</span>
            </label>
            <label class="aweni-cal-toggle">
              <input type="checkbox" data-toggle="showIslamic" ${p.showIslamic ? 'checked' : ''}>
              <span>${t('calendar_layer_islamic')}</span>
            </label>
            <label class="aweni-cal-toggle">
              <input type="checkbox" data-toggle="showPersonal" ${p.showPersonal ? 'checked' : ''}>
              <span>${t('calendar_layer_personal')}</span>
            </label>
          </section>

          <section class="aweni-cal-setrow">
            <label class="aweni-cal-label" for="aweni-cal-cc">${t('calendar_setting_country')}</label>
            <input id="aweni-cal-cc" class="aweni-cal-input" type="text" maxlength="2"
                   value="${escapeHtml(p.countryCode ?? '')}" placeholder="MA"
                   aria-describedby="aweni-cal-cc-hint">
            <div id="aweni-cal-cc-hint" class="aweni-cal-hint">${t('calendar_setting_country_hint')}</div>
          </section>

          <button type="button" class="aweni-cal-savebtn" data-action="save-settings">
            ${t('calendar_save')}
          </button>
        </div>
      </div>
    `;
  }

  private segBtn(name: string, value: string, label: string, on: boolean): string {
    return `<button type="button" class="aweni-cal-seg ${on ? 'is-on' : ''}"
                    data-seg="${name}" data-value="${value}"
                    role="radio" aria-checked="${on}">${label}</button>`;
  }

  private bindSettings(): void {
    if (!this.overlay) return;
    this.overlay.querySelector('[data-action="back-from-settings"]')?.addEventListener('click', () => {
      this.settingsOpen = false; this.render();
    });
    this.overlay.querySelector('[data-action="close"]')?.addEventListener('click', () => this.close());
    this.overlay.querySelectorAll('[data-seg]').forEach((el) => {
      el.addEventListener('click', () => {
        const name = (el as HTMLElement).getAttribute('data-seg') as keyof CalendarPrefs;
        const value = (el as HTMLElement).getAttribute('data-value') || '';
        if (name === 'mode') {
          if (value === 'gregorian' || value === 'both' || value === 'hijri') this.prefs.mode = value;
        } else if (name === 'weekStart') {
          const n = parseInt(value, 10);
          if (n === 0 || n === 1 || n === 6) this.prefs.weekStart = n;
        }
        this.savePrefs();
        this.render();
      });
    });
    this.overlay.querySelectorAll('[data-toggle]').forEach((el) => {
      el.addEventListener('change', () => {
        const key = (el as HTMLElement).getAttribute('data-toggle') as keyof CalendarPrefs;
        const checked = (el as HTMLInputElement).checked;
        if (key === 'showHolidays' || key === 'showIslamic' || key === 'showPersonal') {
          this.prefs[key] = checked;
          this.savePrefs();
          this.refreshMonthEvents();
        }
      });
    });
    this.overlay.querySelector('[data-action="save-settings"]')?.addEventListener('click', () => {
      const inp = this.overlay?.querySelector<HTMLInputElement>('#aweni-cal-cc');
      const cc = (inp?.value || '').trim().toUpperCase().slice(0, 2);
      this.prefs.countryCode = cc.length === 2 ? cc : null;
      this.savePrefs();
      this.settingsOpen = false;
      this.render();
      this.refreshMonthEvents();
    });
  }

  // -------- Add personal event ------------------------------------------

  private renderAddEvent(t: (k: string, p?: Record<string, string | number>) => string, _isRtl: boolean): string {
    return `
      <div class="aweni-cal-sheet" role="document">
        <header class="aweni-cal-header">
          <button type="button" class="aweni-cal-iconbtn" data-action="back-from-add" aria-label="${t('common_back')}">
            ${this.iconChevron('left')}
          </button>
          <div class="aweni-cal-titlewrap">
            <h2 class="aweni-cal-title">${t('calendar_add_event')}</h2>
            <div class="aweni-cal-subtitle">${escapeHtml(this.selectedISO)}</div>
          </div>
          <button type="button" class="aweni-cal-iconbtn aweni-cal-close" data-action="close" aria-label="${t('common_close')}">
            ${this.iconX()}
          </button>
        </header>

        <div class="aweni-cal-add-form">
          <label class="aweni-cal-label" for="aweni-cal-evtitle">${t('calendar_event_title')}</label>
          <input id="aweni-cal-evtitle" class="aweni-cal-input" type="text" maxlength="120"
                 placeholder="${t('calendar_event_title_placeholder')}" autocomplete="off">

          <label class="aweni-cal-label" for="aweni-cal-evdesc">${t('calendar_event_desc')}</label>
          <textarea id="aweni-cal-evdesc" class="aweni-cal-input aweni-cal-textarea" rows="3" maxlength="500"
                    placeholder="${t('calendar_event_desc_placeholder')}"></textarea>

          <button type="button" class="aweni-cal-savebtn" data-action="save-event">
            ${t('calendar_save_event')}
          </button>
        </div>
      </div>
    `;
  }

  private bindAddEvent(): void {
    if (!this.overlay) return;
    this.overlay.querySelector('[data-action="back-from-add"]')?.addEventListener('click', () => {
      this.addOpen = false; this.render();
    });
    this.overlay.querySelector('[data-action="close"]')?.addEventListener('click', () => this.close());
    this.overlay.querySelector('[data-action="save-event"]')?.addEventListener('click', () => {
      const title = this.overlay?.querySelector<HTMLInputElement>('#aweni-cal-evtitle')?.value || '';
      const desc = this.overlay?.querySelector<HTMLTextAreaElement>('#aweni-cal-evdesc')?.value || '';
      if (title.trim().length === 0) return;
      this.events.addPersonalEvent(this.selectedISO, title, desc);
      this.addOpen = false;
      this.refreshMonthEvents();
    });
    // Auto-focus
    setTimeout(() => this.overlay?.querySelector<HTMLInputElement>('#aweni-cal-evtitle')?.focus(), 50);
  }

  // -------- State helpers ------------------------------------------------

  private shiftMonth(delta: number): void {
    this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + delta, 1);
    this.refreshMonthEvents();
  }

  private goToday(): void {
    const today = new Date();
    this.cursor = new Date(today.getFullYear(), today.getMonth(), 1);
    this.selectedISO = toISO(today);
    this.refreshMonthEvents();
  }

  private refreshMonthEvents(): void {
    const start = toISO(this.cursor);
    const last = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + 1, 0);
    const end = toISO(last);

    // Sync (cached + offline) — render immediately
    this.monthEvents = this.events.getEventsInRangeSync(start, end, this.prefs.countryCode, {
      includeIslamic: this.prefs.showIslamic,
      includeHolidays: this.prefs.showHolidays,
      includePersonal: this.prefs.showPersonal,
      locale: this.deps.ctx.i18n.locale || 'en',
    });
    this.render();

    // Async — upgrade with fresh holidays
    if (this.prefs.showHolidays && this.prefs.countryCode) {
      this.events.getEventsInRange(start, end, this.prefs.countryCode, {
        includeIslamic: this.prefs.showIslamic,
        includeHolidays: this.prefs.showHolidays,
        includePersonal: this.prefs.showPersonal,
        locale: this.deps.ctx.i18n.locale || 'en',
      }).then((fresh) => {
        this.monthEvents = fresh;
        if (this.overlay && !this.settingsOpen && !this.addOpen) this.render();
      });
    }
  }

  private buildMonthGrid(): GridCell[] {
    const cur = this.cursor;
    const first = new Date(cur.getFullYear(), cur.getMonth(), 1);
    const last = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const ws = this.prefs.weekStart;
    // Day-of-week of `first`, normalized so that the leftmost column is `ws`.
    const offset = (first.getDay() - ws + 7) % 7;
    const totalDays = 42; // 6 rows × 7 cols, always fixed
    const cells: GridCell[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(cur.getFullYear(), cur.getMonth(), 1 - offset + i);
      cells.push({
        date: d,
        iso: toISO(d),
        inMonth: d.getMonth() === cur.getMonth(),
      });
      void last;
    }
    return cells;
  }

  private dayHeaderLabels(locale: string): string[] {
    const out: string[] = [];
    // Use a known Sunday (2024-01-07) as anchor.
    const anchor = new Date(2024, 0, 7);
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + ((this.prefs.weekStart + i) % 7));
      try {
        out.push(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d));
      } catch {
        out.push(d.toLocaleDateString('en-US', { weekday: 'short' }));
      }
    }
    return out;
  }

  private findCell(iso: string): GridCell | null {
    const grid = this.buildMonthGrid();
    return grid.find((c) => c.iso === iso) || null;
  }

  // -------- Prefs persistence -------------------------------------------

  private loadPrefs(defaultCC: string | null): CalendarPrefs {
    try {
      const raw = localStorage.getItem(PREFS_LS_KEY);
      if (!raw) return { ...DEFAULT_PREFS, countryCode: defaultCC };
      const parsed = JSON.parse(raw);
      const out: CalendarPrefs = { ...DEFAULT_PREFS, countryCode: defaultCC, ...parsed };
      // Defensive: ensure weekStart valid
      if (out.weekStart !== 0 && out.weekStart !== 1 && out.weekStart !== 6) out.weekStart = 1;
      return out;
    } catch {
      return { ...DEFAULT_PREFS, countryCode: defaultCC };
    }
  }

  private savePrefs(): void {
    try {
      localStorage.setItem(PREFS_LS_KEY, JSON.stringify(this.prefs));
    } catch {
      /* quota */
    }
  }

  // -------- Icons (inline svg) -------------------------------------------

  private iconChevron(dir: 'left' | 'right'): string {
    const path = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6';
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
  }
  private iconX(): string {
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
  }
  private iconGear(): string {
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;
  }
  private iconCircle(): string {
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>`;
  }
  private iconPlus(): string {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
  }
  private iconTrash(): string {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
  }

  // -------- Styles -------------------------------------------------------

  private injectStyles(): void {
    if (document.getElementById('aweni-cal-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-cal-styles';
    style.textContent = `
      .aweni-cal-overlay {
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.55);
        z-index: 1000;
        display: flex; align-items: flex-end; justify-content: center;
        padding: 0;
        animation: awCalFadeIn 160ms ease;
      }
      @media (min-width: 640px) {
        .aweni-cal-overlay { align-items: center; padding: 24px; }
      }
      @keyframes awCalFadeIn { from { opacity: 0; } to { opacity: 1; } }
      .aweni-cal-sheet {
        background: var(--ln-bg-card, var(--card-bg, #FFFFFF));
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
        width: 100%;
        max-width: 520px;
        max-height: 92vh;
        border-radius: 22px 22px 0 0;
        display: flex; flex-direction: column;
        overflow: hidden;
        animation: awCalSlideUp 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
      }
      @media (min-width: 640px) {
        .aweni-cal-sheet { border-radius: 22px; max-height: 88vh; }
      }
      @keyframes awCalSlideUp {
        from { transform: translateY(24px); opacity: 0.6; }
        to { transform: translateY(0); opacity: 1; }
      }
      .aweni-cal-header {
        display: flex; align-items: center; gap: 6px;
        padding: 12px 12px 8px;
        border-bottom: 1px solid var(--ln-border, var(--border, #E5E7EB));
        flex-shrink: 0;
      }
      .aweni-cal-titlewrap { flex: 1; min-width: 0; padding: 0 6px; }
      .aweni-cal-title {
        margin: 0; font-size: 17px; font-weight: 700;
        text-transform: capitalize;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .aweni-cal-subtitle {
        margin-top: 2px; font-size: 12px;
        color: var(--ln-text-secondary, #64748B);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      /* Country/authority reference line — surfaces WHICH moonsighting
         body the displayed Hijri dates align with. Critical for trust. */
      .aweni-cal-ref {
        display: inline-flex; align-items: center; gap: 4px;
        margin-top: 2px;
        font-size: 10px; line-height: 1.3;
        color: var(--ln-text-secondary, #64748B);
        text-transform: none; letter-spacing: 0;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        max-width: 100%;
      }
      .aweni-cal-ref-flag { font-size: 11px; }
      .aweni-cal-ref-city { font-weight: 600; color: var(--ln-text-primary, #1E293B); }
      .aweni-cal-ref-sep { opacity: 0.4; margin: 0 1px; }
      .aweni-cal-ref-auth { opacity: 0.85; }
      /* Prompt variant (button) — shown when no country is set. */
      .aweni-cal-ref-prompt {
        background: var(--ln-accent, #0A0A0A)10;
        border: 1px solid var(--ln-accent, #0A0A0A)30;
        border-radius: 999px;
        padding: 4px 10px;
        cursor: pointer;
        font: inherit;
        color: var(--ln-accent, #0A0A0A);
        transition: background 120ms ease, transform 120ms ease;
      }
      .aweni-cal-ref-prompt:hover {
        background: var(--ln-accent, #0A0A0A)18;
      }
      .aweni-cal-ref-prompt:active { transform: scale(0.98); }
      .aweni-cal-ref-prompt .aweni-cal-ref-auth {
        font-weight: 600;
        opacity: 1;
        color: var(--ln-accent, #0A0A0A);
      }
      .aweni-cal-ref-prompt .aweni-cal-ref-cta {
        margin-inline-start: 4px;
        opacity: 0.7;
      }
      /* Future-event uncertainty hint — appears in details panel for any
         day whose Hijri date is more than 1 lunar month away from today. */
      .aweni-cal-details-uncertain {
        margin-top: 6px;
        padding: 6px 10px;
        border-radius: 8px;
        background: #f59e0b15;
        border: 1px solid #f59e0b40;
        font-size: 11px;
        line-height: 1.4;
        color: #92400e;
      }
      .aweni-cal-iconbtn {
        flex-shrink: 0;
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 10px;
        background: transparent;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
        border: none; cursor: pointer;
        transition: background 120ms ease;
      }
      .aweni-cal-iconbtn:hover { background: var(--ln-bg-secondary, rgba(0,0,0,0.05)); }
      .aweni-cal-iconbtn:active { transform: scale(0.94); }
      .aweni-cal-iconbtn:focus-visible {
        outline: 2px solid var(--ln-accent, #0A0A0A);
        outline-offset: 2px;
      }
      .aweni-cal-close { color: var(--ln-text-secondary, #64748B); }

      /* Grid */
      .aweni-cal-grid-wrap {
        padding: 6px 12px 12px;
        flex-shrink: 0;
      }
      .aweni-cal-dayheaders {
        display: grid; grid-template-columns: repeat(7, 1fr);
        gap: 0;
        margin-bottom: 4px;
      }
      .aweni-cal-dayheader {
        font-size: 11px; font-weight: 600;
        color: var(--ln-text-secondary, #64748B);
        text-align: center;
        padding: 6px 0;
        text-transform: uppercase;
        letter-spacing: 0.4px;
      }
      .aweni-cal-grid {
        display: grid; grid-template-columns: repeat(7, 1fr);
        gap: 4px;
      }
      .aweni-cal-cell {
        position: relative;
        aspect-ratio: 1 / 1;
        min-height: 44px;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 10px;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 2px;
        font: inherit; color: inherit;
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease;
      }
      .aweni-cal-cell:hover { background: var(--ln-bg-secondary, rgba(0,0,0,0.04)); }
      .aweni-cal-cell:focus-visible {
        outline: 2px solid var(--ln-accent, #0A0A0A);
        outline-offset: 1px;
      }
      .aweni-cal-cell.is-other { opacity: 0.34; }
      .aweni-cal-cell.is-today { border-color: var(--ln-accent, #0A0A0A); }
      .aweni-cal-cell.is-selected {
        background: var(--ln-accent, #0A0A0A);
        color: var(--ln-accent-text, #fff);
      }
      .aweni-cal-cell.is-selected .aweni-cal-cell-hijri { color: var(--ln-accent-text, #fff); opacity: 0.85; }
      .aweni-cal-cell-greg {
        font-size: 14px; font-weight: 600; line-height: 1;
      }
      .aweni-cal-cell-hijri {
        font-size: 10px; font-weight: 500; line-height: 1;
        color: var(--ln-text-secondary, #64748B);
      }
      .aweni-cal-dots {
        position: absolute; bottom: 4px; left: 50%;
        transform: translateX(-50%);
        display: flex; gap: 2px;
      }
      .aweni-cal-dot {
        display: inline-block;
        width: 4px; height: 4px;
        border-radius: 9999px;
      }

      /* Details */
      .aweni-cal-details {
        flex: 1;
        overflow-y: auto;
        border-top: 1px solid var(--ln-border, var(--border, #E5E7EB));
        padding: 12px 18px 18px;
        background: var(--ln-bg-primary, transparent);
      }
      .aweni-cal-details-head { margin-bottom: 8px; }
      .aweni-cal-details-title {
        font-size: 14px; font-weight: 700; line-height: 1.3;
      }
      .aweni-cal-details-sub {
        font-size: 12px;
        color: var(--ln-text-secondary, #64748B);
        margin-top: 2px;
      }
      .aweni-cal-events {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-direction: column; gap: 6px;
      }
      .aweni-cal-event {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 12px;
        background: var(--ln-bg-card, var(--card-bg, #FFFFFF));
        border: 1px solid var(--ln-border, var(--border, #E5E7EB));
        border-radius: 12px;
        font-size: 13px;
      }
      .aweni-cal-event-empty {
        color: var(--ln-text-secondary, #64748B);
        text-align: center; justify-content: center;
        padding: 14px 12px;
      }
      .aweni-cal-event-bullet {
        flex-shrink: 0;
        width: 8px; height: 8px;
        border-radius: 9999px;
        margin-top: 5px;
      }
      .aweni-cal-event-body { flex: 1; min-width: 0; }
      .aweni-cal-event-title { font-weight: 600; line-height: 1.3; }
      .aweni-cal-event-meta {
        margin-top: 2px; font-size: 11px;
        color: var(--ln-text-secondary, #64748B);
      }
      .aweni-cal-event-del {
        flex-shrink: 0;
        width: 28px; height: 28px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 8px;
        background: transparent; border: none; cursor: pointer;
        color: var(--ln-text-secondary, #64748B);
      }
      .aweni-cal-event-del:hover { background: rgba(220, 38, 38, 0.1); color: #DC2626; }
      .aweni-cal-add {
        margin-top: 10px;
        display: inline-flex; align-items: center; gap: 6px;
        padding: 10px 14px;
        background: var(--ln-bg-card, var(--card-bg, #FFFFFF));
        border: 1px dashed var(--ln-border, var(--border, #CBD5E1));
        border-radius: 12px;
        font-size: 13px; font-weight: 600;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease;
      }
      .aweni-cal-add:hover {
        background: var(--ln-bg-secondary, rgba(0,0,0,0.04));
        border-color: var(--ln-accent, #0A0A0A);
      }

      /* Settings + Add forms */
      .aweni-cal-settings, .aweni-cal-add-form {
        flex: 1; overflow-y: auto;
        padding: 14px 18px 24px;
        display: flex; flex-direction: column; gap: 18px;
      }
      .aweni-cal-setrow { display: flex; flex-direction: column; gap: 8px; }
      .aweni-cal-label {
        font-size: 12px; font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: var(--ln-text-secondary, #64748B);
      }
      .aweni-cal-segmented {
        display: flex; gap: 4px;
        padding: 4px;
        background: var(--ln-bg-secondary, rgba(0,0,0,0.04));
        border-radius: 12px;
      }
      .aweni-cal-seg {
        flex: 1; padding: 8px 10px;
        background: transparent; border: none;
        border-radius: 8px;
        font-size: 13px; font-weight: 600;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
        cursor: pointer;
        transition: background 120ms ease;
      }
      .aweni-cal-seg.is-on {
        background: var(--ln-bg-card, #FFFFFF);
        box-shadow: 0 1px 3px rgba(0,0,0,0.08);
      }
      .aweni-cal-toggle {
        display: flex; align-items: center; gap: 10px;
        font-size: 14px; font-weight: 500;
        cursor: pointer;
      }
      .aweni-cal-toggle input { width: 18px; height: 18px; cursor: pointer; }
      .aweni-cal-input {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid var(--ln-border, var(--border, #E5E7EB));
        border-radius: 10px;
        background: var(--ln-bg-card, var(--card-bg, #FFFFFF));
        color: inherit;
        font: inherit;
        font-size: 14px;
        outline: none;
      }
      .aweni-cal-input:focus { border-color: var(--ln-accent, #0A0A0A); }
      .aweni-cal-textarea { resize: vertical; min-height: 80px; }
      .aweni-cal-hint {
        font-size: 11px;
        color: var(--ln-text-secondary, #64748B);
      }
      .aweni-cal-savebtn {
        margin-top: 6px;
        padding: 12px 16px;
        background: var(--ln-accent, #0A0A0A);
        color: var(--ln-accent-text, #fff);
        border: none; border-radius: 12px;
        font-size: 15px; font-weight: 700;
        cursor: pointer;
        min-height: 48px;
      }
      .aweni-cal-savebtn:active { transform: scale(0.98); }
    `;
    document.head.appendChild(style);
  }
}

interface GridCell {
  date: Date;
  iso: string;
  inMonth: boolean;
}

function categoryColor(c: CalendarEvent['category']): string {
  switch (c) {
    case 'holiday':  return '#DC2626'; // red
    case 'islamic':  return '#9C8348'; // champagne (LN brand)
    case 'cultural': return '#0EA5E9'; // sky
    case 'personal': return '#16A34A'; // green
  }
}

function unique<T>(arr: T[]): T[] { return Array.from(new Set(arr)); }

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseISO(iso: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return isNaN(d.getTime()) ? null : d;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}
